<script lang="ts">
  import {
    user, authReady, signInWithGoogle, signInWithEmail, registerWithEmail, signOutUser,
  } from '../lib/auth';

  let showEmail = false;
  let mode: 'in' | 'up' = 'in';
  let email = '';
  let password = '';
  let error = '';
  let busy = false;

  function friendly(e: unknown): string {
    const code = (e as { code?: string })?.code || '';
    if (code.includes('popup-closed') || code.includes('cancelled-popup')) return '';
    if (code.includes('invalid-credential') || code.includes('wrong-password') || code.includes('user-not-found')) return 'Wrong email or password.';
    if (code.includes('email-already-in-use')) return 'That email already has an account. Sign in instead.';
    if (code.includes('weak-password')) return 'Password must be at least 6 characters.';
    if (code.includes('invalid-email')) return 'That doesn’t look like a valid email.';
    return (e as Error)?.message || 'Something went wrong.';
  }

  async function google() {
    error = ''; busy = true;
    try { await signInWithGoogle(); } catch (e) { error = friendly(e); } finally { busy = false; }
  }

  async function emailSubmit() {
    error = ''; busy = true;
    try {
      if (mode === 'up') await registerWithEmail(email, password);
      else await signInWithEmail(email, password);
      email = ''; password = ''; showEmail = false;
    } catch (e) { error = friendly(e); } finally { busy = false; }
  }
</script>

{#if $authReady}
  {#if $user}
    <div class="account">
      {#if $user.photoURL}
        <img class="account-avatar" src={$user.photoURL} alt="" referrerpolicy="no-referrer" />
      {:else}
        <div class="account-avatar account-avatar--blank" aria-hidden="true">{($user.displayName || $user.email || '?').slice(0, 1).toUpperCase()}</div>
      {/if}
      <div class="account-info">
        <div class="account-name">{$user.displayName || $user.email}</div>
        <div class="account-sub">Synced to your account</div>
      </div>
      <button class="account-signout" on:click={signOutUser}>Sign out</button>
    </div>
  {:else}
    <div class="account-signin">
      <button class="google-btn" on:click={google} disabled={busy}>
        <span class="google-g" aria-hidden="true">G</span> Sign in with Google
      </button>
      <button class="link-btn" type="button" on:click={() => (showEmail = !showEmail)}>
        {showEmail ? 'hide email sign-in' : 'or use email'}
      </button>
      {#if showEmail}
        <form class="email-form" on:submit|preventDefault={emailSubmit}>
          <input type="email" bind:value={email} placeholder="you@example.com" autocomplete="email" required />
          <input type="password" bind:value={password} placeholder="Password" autocomplete={mode === 'up' ? 'new-password' : 'current-password'} required />
          <div class="button-row">
            <button type="submit" disabled={busy}>{mode === 'up' ? 'Create account' : 'Sign in'}</button>
            <button type="button" class="link-btn" on:click={() => (mode = mode === 'in' ? 'up' : 'in')}>
              {mode === 'in' ? 'need an account?' : 'have one?'}
            </button>
          </div>
        </form>
      {/if}
      {#if error}<p class="account-error">{error}</p>{/if}
      <p class="help-text" style="margin-top: 6px;">Sign in to save your map across devices. It’s optional. Everything works locally too.</p>
    </div>
  {/if}
{/if}

<style>
  .account {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .account-avatar {
    width: 34px; height: 34px; border-radius: 50%;
    flex: none; object-fit: cover; border: 1px solid var(--line);
  }
  .account-avatar--blank {
    display: flex; align-items: center; justify-content: center;
    background: var(--accent-soft); color: var(--accent); font-weight: 700; font-size: 15px;
  }
  .account-info { min-width: 0; flex: 1; }
  .account-name {
    font-size: 13px; font-weight: 600; color: var(--ink);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .account-sub { font-size: 10.5px; color: var(--ink-soft); }
  .account-signout {
    flex: none; background: transparent; color: var(--ink-soft);
    border: 1px solid var(--line); padding: 6px 10px; font-size: 11.5px;
  }
  .account-signout:hover { background: rgba(29,53,40,0.04); color: var(--ink); }

  .google-btn {
    width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px;
    background: #fff; color: #1f1f1f; border: 1px solid var(--line);
    padding: 10px 14px; font-size: 13px; font-weight: 500;
  }
  .google-btn:hover { background: #f7f4ec; }
  .google-g {
    font-weight: 700; color: #4285f4;
    font-family: Arial, sans-serif;
  }
  .link-btn {
    background: transparent; border: none; color: var(--accent);
    font-size: 11.5px; padding: 4px 0; text-decoration: underline; cursor: pointer;
  }
  .email-form { margin-top: 8px; display: flex; flex-direction: column; gap: 6px; }
  .account-error { color: var(--warn); font-size: 11.5px; margin: 6px 0 0; }
</style>
